import React, { FC, useCallback, useMemo } from 'react'
import { Box, Text, Flex, Spacer, VStack, Button, Spinner, Image } from '@chakra-ui/react'
import { PostalCode } from '../postal-code/postal-code'
import { addAnswer, selectAnswers, selectScenarioId, useSelector } from '../../state'
import { Answers, cardImageMap, Steps } from '../../state/interfaces'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { setCookie } from '../../scenarios/storage'

export interface InternetUsageProps {
  name: string
  grandImage?: string
}

export const InternetUsage: FC<InternetUsageProps> = ({ name, grandImage }) => {
  const { t } = useTranslation()
  const answers: Answers = useSelector(selectAnswers)
  const scenarioId: string | undefined = useSelector(selectScenarioId)
  const dispatch = useDispatch()
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])

  const onSubmit = useCallback(
    async (postalCode: string) => {
      if (postalCode) {
        setCookie('internetSearchPostalcode', postalCode)
        dispatch(addAnswer({ stepId: Steps.POSTAL_CODE, answerIds: [postalCode, 'finishScenario'], hidden: true }))
      } else {
        dispatch(addAnswer({ stepId: Steps.POSTAL_CODE, answerIds: [postalCode], hidden: false }))
      }
    },
    [dispatch],
  )

  return (
    <VStack alignItems={'center'} spacing={0}>
      <Flex alignItems={'center'} direction={['column', 'column', 'row', 'row']}>
        <Box maxWidth={375} padding={3}>
          <Text fontSize="xxs" fontWeight="bold" color="green.500" textTransform="uppercase" marginBottom={2}>
            {t('common_complete')}
          </Text>
          <Text fontSize="sm" marginBottom={2}>
            {answers && answers['internetSpeed-or-help'] && answers['internetSpeed-or-help']![0] === 'speed-search'
              ? t('common_your-selected-internet-speed')
              : t('common_we-estimate-your-internet-speed')}
          </Text>
          <Text fontSize="5xl" fontWeight="bold" lineHeight={15} marginBottom={2}>
            {answers['internet-speed'] && answers['internet-speed']![0] !== 'custom-speed'
              ? answers['internet-speed']
              : answers['download-speed'] ?? 0}{' '}
            {t('common_mbps')}
          </Text>
          <Text color="darkGrey" fontSize="xs" marginBottom={9}>
            {t('common_enter-your-postal-code')}
          </Text>
        </Box>
        <Spacer minWidth={150} />
        <VStack alignItems={'end'}>
          {answers && answers['postal-code'] && answers['postal-code'][0] ? (
            <Spinner />
          ) : (
            <PostalCode onSubmit={onSubmit} name={name} />
          )}
          {scenarioId &&
            (scenarioId === 'internet-f' || scenarioId === 'internet-g') &&
            !(answers && answers['postal-code'] && answers['postal-code'][0]) && (
              <Box>
                <Button
                  onClick={() => onSubmit('')}
                  marginTop={2}
                  textColor="green.500"
                  fontSize={22}
                  tabIndex={0}
                  _focus={{
                    borderColor: 'brand',
                    boxShadow: '0 0 0 3px #5bce4c',
                  }}
                >
                  {t('common_skip')}
                </Button>
              </Box>
            )}
        </VStack>
      </Flex>
      <Image src={image} alt="image" width={['60%', '35%']} />
    </VStack>
  )
}
